import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  Activity, 
  Users, 
  Clock, 
  MousePointerClick, 
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Globe,
  Smartphone,
  Monitor
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DateRangeFilter } from '@/components/analytics/DateRangeFilter';
import { GeographyMap } from '@/components/analytics/GeographyMap';
import { UserJourneyFlow } from '@/components/analytics/UserJourneyFlow';

interface SessionData {
  id: string;
  session_id: string;
  user_id: string | null;
  page_route: string;
  time_spent_seconds: number;
  interactions: any;
  cart_data: any;
  abandoned_cart: boolean;
  created_at: string;
  updated_at: string;
  country_code: string | null;
  country_name: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
}

interface KPIData {
  totalSessions: number;
  uniqueUsers: number;
  avgTimeSpent: number;
  totalInteractions: number;
  cartAbandonment: number;
  conversionRate: number;
  totalEvents: number;
}

interface PageMetrics {
  route: string;
  visits: number;
  avgTime: number;
}

interface LocationData {
  latitude: number;
  longitude: number;
  city: string;
  country: string;
  count: number;
}

interface JourneyStep {
  page: string;
  nextPages: { page: string; count: number }[];
}

export function AnalyticsDashboard() {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<SessionData[]>([]);
  const [kpis, setKpis] = useState<KPIData | null>(null);
  const [pageMetrics, setPageMetrics] = useState<PageMetrics[]>([]);
  const [locationData, setLocationData] = useState<LocationData[]>([]);
  const [journeyData, setJourneyData] = useState<JourneyStep[]>([]);
  const [deviceData, setDeviceData] = useState<{ name: string; value: number }[]>([]);
  const [ensembleClicks, setEnsembleClicks] = useState<{ name: string; value: number }[]>([]);
  const [avgPriceByEnsemble, setAvgPriceByEnsemble] = useState<{ name: string; avgPrice: number; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const { toast } = useToast();

  useEffect(() => {
    loadAnalytics();

    const channel = supabase
      .channel('session-analytics-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'session_analytics',
        },
        () => {
          loadAnalytics();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (!startDate && !endDate) {
      setFilteredSessions(sessions);
    } else {
      const filtered = sessions.filter(session => {
        const sessionDate = new Date(session.created_at);
        if (startDate && sessionDate < startDate) return false;
        if (endDate && sessionDate > endDate) return false;
        return true;
      });
      setFilteredSessions(filtered);
    }
  }, [sessions, startDate, endDate]);

  useEffect(() => {
    calculateMetrics(filteredSessions);
  }, [filteredSessions]);

  const handleDateRangeChange = (start: Date | undefined, end: Date | undefined) => {
    setStartDate(start);
    setEndDate(end);
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const { data: sessionData, error } = await supabase
        .from('session_analytics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      const { count: eventCount } = await supabase
        .from('contact_submissions')
        .select('*', { count: 'exact', head: true });

      setSessions(sessionData || []);
      setKpis(prev => prev ? { ...prev, totalEvents: eventCount || 0 } : null);
    } catch (error: any) {
      console.error('Failed to load analytics:', error);
      toast({
        title: 'Error Loading Analytics',
        description: error.message || 'Failed to load session analytics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (sessionData: SessionData[]) => {
    if (!sessionData || sessionData.length === 0) {
      setKpis(null);
      setPageMetrics([]);
      setLocationData([]);
      setJourneyData([]);
      setDeviceData([]);
      return;
    }

    const totalSessions = sessionData.length;
    const uniqueUsers = new Set(sessionData.map(s => s.user_id || s.session_id)).size;
    const totalTime = sessionData.reduce((sum, s) => sum + (s.time_spent_seconds || 0), 0);
    const avgTimeSpent = totalSessions > 0 ? Math.round(totalTime / totalSessions) : 0;
    const totalInteractions = sessionData.reduce((sum, s) => sum + ((s.interactions as any[])?.length || 0), 0);
    const cartsAbandoned = sessionData.filter(s => s.abandoned_cart).length;
    const cartsCreated = sessionData.filter(s => s.cart_data && Array.isArray(s.cart_data) && s.cart_data.length > 0).length;
    const cartAbandonment = cartsCreated > 0 ? Math.round((cartsAbandoned / cartsCreated) * 100) : 0;
    const conversionRate = cartsCreated > 0 ? Math.round(((cartsCreated - cartsAbandoned) / cartsCreated) * 100) : 0;

    setKpis({
      totalSessions,
      uniqueUsers,
      avgTimeSpent,
      totalInteractions,
      cartAbandonment,
      conversionRate,
      totalEvents: kpis?.totalEvents || 0,
    });

    const pageMap = new Map<string, { visits: number; totalTime: number }>();
    sessionData.forEach(s => {
      const existing = pageMap.get(s.page_route) || { visits: 0, totalTime: 0 };
      pageMap.set(s.page_route, {
        visits: existing.visits + 1,
        totalTime: existing.totalTime + (s.time_spent_seconds || 0),
      });
    });

    const metrics = Array.from(pageMap.entries())
      .map(([route, data]) => ({
        route,
        visits: data.visits,
        avgTime: Math.round(data.totalTime / data.visits),
      }))
      .sort((a, b) => b.visits - a.visits);

    setPageMetrics(metrics);

    const locationMap = new Map<string, LocationData>();
    sessionData.forEach(s => {
      if (s.latitude && s.longitude && s.city && s.country_name) {
        const key = `${s.city}-${s.country_name}`;
        const existing = locationMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          locationMap.set(key, {
            latitude: s.latitude,
            longitude: s.longitude,
            city: s.city,
            country: s.country_name,
            count: 1,
          });
        }
      }
    });

    setLocationData(Array.from(locationMap.values()));

    const sortedSessions = [...sessionData].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    const journeyMap = new Map<string, Map<string, number>>();
    const sessionGroups = new Map<string, SessionData[]>();
    
    sortedSessions.forEach(s => {
      const sessions = sessionGroups.get(s.session_id) || [];
      sessions.push(s);
      sessionGroups.set(s.session_id, sessions);
    });

    sessionGroups.forEach(sessions => {
      for (let i = 0; i < sessions.length - 1; i++) {
        const currentPage = sessions[i].page_route;
        const nextPage = sessions[i + 1].page_route;
        
        if (!journeyMap.has(currentPage)) {
          journeyMap.set(currentPage, new Map());
        }
        
        const nextPages = journeyMap.get(currentPage)!;
        nextPages.set(nextPage, (nextPages.get(nextPage) || 0) + 1);
      }
    });

    const journey: JourneyStep[] = Array.from(journeyMap.entries()).map(([page, nextPages]) => ({
      page,
      nextPages: Array.from(nextPages.entries())
        .map(([p, count]) => ({ page: p, count }))
        .sort((a, b) => b.count - a.count),
    }));

    setJourneyData(journey);

    const deviceMap = new Map<string, number>();
    const ensembleClickMap = new Map<string, number>();
    const ensemblePriceMap = new Map<string, { total: number; count: number }>();
    
    sessionData.forEach(s => {
      const device = s.device_type || 'unknown';
      deviceMap.set(device, (deviceMap.get(device) || 0) + 1);

      if (Array.isArray(s.interactions)) {
        s.interactions.forEach((interaction: any) => {
          if (interaction.type === 'click' && interaction.target === 'ensemble_package' && interaction.details?.ensembleSize) {
            const size = interaction.details.ensembleSize;
            ensembleClickMap.set(size, (ensembleClickMap.get(size) || 0) + 1);
          }
        });
      }

      if (s.cart_data && Array.isArray(s.cart_data)) {
        s.cart_data.forEach((item: any) => {
          if (item.ensembleSize && item.price) {
            const existing = ensemblePriceMap.get(item.ensembleSize) || { total: 0, count: 0 };
            ensemblePriceMap.set(item.ensembleSize, {
              total: existing.total + item.price,
              count: existing.count + 1,
            });
          }
        });
      }
    });

    const deviceBreakdown = Array.from(deviceMap.entries()).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
    }));

    const ensembleClicksData = Array.from(ensembleClickMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name: `${name}-piece`, value }));

    const avgPrices = Array.from(ensemblePriceMap.entries()).map(([name, data]) => ({
      name: `${name}-piece`,
      avgPrice: Math.round(data.total / data.count),
      count: data.count,
    }));

    setDeviceData(deviceBreakdown);
    setEnsembleClicks(ensembleClicksData);
    setAvgPriceByEnsemble(avgPrices);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Activity className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h3 className="text-2xl font-bold mb-2 flex items-center gap-3">
          <Activity className="w-6 h-6 text-primary" />
          Analytics Dashboard
        </h3>
        <p className="text-muted-foreground text-sm">
          Real-time insights and metrics from user sessions
        </p>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Filter by Date Range</CardTitle>
        </CardHeader>
        <CardContent>
          <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
        </CardContent>
      </Card>

      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.totalSessions}</div>
              <p className="text-xs text-muted-foreground">Across all pages</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.uniqueUsers}</div>
              <p className="text-xs text-muted-foreground">Unique sessions/users</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg. Time Spent</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatDuration(kpis.avgTimeSpent)}</div>
              <p className="text-xs text-muted-foreground">Per session</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Interactions</CardTitle>
              <MousePointerClick className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.totalInteractions}</div>
              <p className="text-xs text-muted-foreground">Clicks, scrolls, inputs</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cart Abandonment</CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                {kpis.cartAbandonment}%
                {kpis.cartAbandonment > 50 ? (
                  <TrendingUp className="h-4 w-4 text-destructive" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-green-500" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {kpis.cartAbandonment > 50 ? 'High abandonment rate' : 'Good retention'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{kpis.conversionRate}%</div>
              <p className="text-xs text-muted-foreground">Completed checkouts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Events Inquired</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.totalEvents}</div>
              <p className="text-xs text-muted-foreground">Total event inquiries</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Geography Map */}
      {locationData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Geographic Distribution
            </CardTitle>
            <CardDescription>Where your users are from</CardDescription>
          </CardHeader>
          <CardContent>
            <GeographyMap locations={locationData} />
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {locationData.slice(0, 4).map((loc, idx) => (
                <div key={idx} className="p-3 bg-muted rounded-lg">
                  <p className="font-semibold text-sm">{loc.city}</p>
                  <p className="text-xs text-muted-foreground">{loc.country}</p>
                  <p className="text-lg font-bold mt-1">{loc.count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Journey Flow */}
      <UserJourneyFlow journeyData={journeyData} />

      {/* Device & Browser Stats */}
      {deviceData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Device Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  value: {
                    label: "Sessions",
                    color: "hsl(var(--primary))",
                  },
                }}
                className="h-[250px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deviceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="hsl(var(--primary))"
                      dataKey="value"
                    >
                      {deviceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Demographics Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold mb-2">Top Countries</h4>
                  {locationData.slice(0, 5).map((loc, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2">
                      <span className="text-sm">{loc.country}</span>
                      <Badge variant="secondary">{loc.count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ensemble Package Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {ensembleClicks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Most Clicked Ensemble Packages
              </CardTitle>
              <CardDescription>Click tracking by ensemble size</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  value: {
                    label: "Clicks",
                    color: "hsl(var(--primary))",
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ensembleClicks}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        )}

        {avgPriceByEnsemble.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Average Cart Price by Ensemble Size
              </CardTitle>
              <CardDescription>Price analysis by package type</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  avgPrice: {
                    label: "Avg Price",
                    color: "hsl(var(--chart-2))",
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={avgPriceByEnsemble}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip 
                      content={<ChartTooltipContent />}
                      formatter={(value: any) => [`$${value.toLocaleString()}`, 'Avg Price']}
                    />
                    <Bar dataKey="avgPrice" fill="hsl(var(--chart-2))" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {avgPriceByEnsemble.map((item) => (
                  <div key={item.name} className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium">{item.name}</p>
                    <p className="text-lg font-bold">${item.avgPrice.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{item.count} in carts</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Page Performance Chart */}
      {pageMetrics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Page Performance</CardTitle>
            <CardDescription>Visit count by page route</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                visits: {
                  label: "Visits",
                  color: "hsl(var(--primary))",
                },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pageMetrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="route" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="visits" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Key Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Key Insights</CardTitle>
          <CardDescription>Data-driven recommendations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {kpis && kpis.cartAbandonment > 60 && (
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-destructive mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">High Cart Abandonment</h4>
                  <p className="text-sm text-muted-foreground">
                    {kpis.cartAbandonment}% of carts are abandoned. Consider simplifying checkout or adding exit-intent offers.
                  </p>
                </div>
              </div>
            )}
            
            {kpis && kpis.avgTimeSpent < 30 && (
              <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">Low Engagement Time</h4>
                  <p className="text-sm text-muted-foreground">
                    Average session time is {formatDuration(kpis.avgTimeSpent)}. Consider improving content engagement.
                  </p>
                </div>
              </div>
            )}

            {pageMetrics.length > 0 && (
              <div className="flex items-start gap-3 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">Top Performing Page</h4>
                  <p className="text-sm text-muted-foreground">
                    <strong>{pageMetrics[0].route}</strong> has {pageMetrics[0].visits} visits with avg. time of {formatDuration(pageMetrics[0].avgTime)}.
                  </p>
                </div>
              </div>
            )}

            {kpis && kpis.conversionRate > 40 && (
              <div className="flex items-start gap-3 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">Strong Conversion Rate</h4>
                  <p className="text-sm text-muted-foreground">
                    {kpis.conversionRate}% conversion rate is excellent. Continue optimizing user experience.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Session Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Sessions</CardTitle>
          <CardDescription>Latest 100 user sessions with detailed metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session ID</TableHead>
                  <TableHead>Page</TableHead>
                  <TableHead>Time Spent</TableHead>
                  <TableHead>Interactions</TableHead>
                  <TableHead>Cart Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No session data available
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSessions.slice(0, 100).map((session) => (
                    <TableRow key={session.id}>
                      <TableCell className="font-mono text-xs">
                        {session.session_id.substring(0, 8)}...
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{session.page_route}</Badge>
                      </TableCell>
                      <TableCell>{formatDuration(session.time_spent_seconds || 0)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {(session.interactions as any[])?.length || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {session.cart_data && Array.isArray(session.cart_data) && session.cart_data.length > 0 ? (
                          <Badge variant={session.abandoned_cart ? "destructive" : "default"}>
                            {session.abandoned_cart ? "Abandoned" : "Active"}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(session.created_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
